export interface StickVector {
  readonly x: number;
  readonly y: number;
}

export type StickSide = "left" | "right";

const DEAD_ZONE = 0.12;
const CHANGE_EPSILON = 0.015;

export class DualStickArbiter {
  private readonly vectors: Record<StickSide, StickVector> = {
    left: { x: 0, y: 0 },
    right: { x: 0, y: 0 },
  };
  private readonly changedAt: Record<StickSide, number> = { left: -1, right: -1 };

  update(side: StickSide, vector: StickVector, timestampMs: number): void {
    const normalized = normalizeStick(vector);
    const previous = this.vectors[side];
    if (Math.hypot(previous.x - normalized.x, previous.y - normalized.y) <= CHANGE_EPSILON) return;
    this.vectors[side] = normalized;
    this.changedAt[side] = timestampMs;
  }

  getActive(): { readonly side: StickSide | null; readonly vector: StickVector } {
    const leftActive = magnitude(this.vectors.left) > DEAD_ZONE;
    const rightActive = magnitude(this.vectors.right) > DEAD_ZONE;

    if (!leftActive && !rightActive) return { side: null, vector: { x: 0, y: 0 } };
    if (leftActive && !rightActive) return { side: "left", vector: this.vectors.left };
    if (rightActive && !leftActive) return { side: "right", vector: this.vectors.right };

    const side: StickSide = this.changedAt.right >= this.changedAt.left ? "right" : "left";
    return { side, vector: this.vectors[side] };
  }
}

function magnitude(vector: StickVector): number {
  return Math.hypot(vector.x, vector.y);
}

function normalizeStick(vector: StickVector): StickVector {
  const length = magnitude(vector);
  if (length <= DEAD_ZONE) return { x: 0, y: 0 };
  if (length <= 1) return { x: vector.x, y: vector.y };
  return { x: vector.x / length, y: vector.y / length };
}
